import { useState, useEffect, useRef } from "react";
import { Button, LinearProgress, makeStyles } from "@material-ui/core";
import MobileLayout from "../MobileLayout";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import { setScore } from "./slice";
import analytics from "../analytics/track";
import { GameFrame } from "./GameFrame";
import { startGameRound, submitGameScore, useBreefDetails } from "./gameService";

// The game gets the whole screen: no card, no margins, nothing of the app around it. The only thing left of the
// app is the time bar along the bottom edge.
const useStyles = makeStyles(() => ({
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100dvh",
    width: "100%",
  },
  gameSpace: {
    flex: 1,
    minHeight: 0,
    width: "100%",
    cursor: "pointer",
    overflow: "hidden",
    backgroundColor: "#FFFFFF",
  },
  timeBar: {
    height: "10px",
    flexShrink: 0,
    backgroundColor: "rgba(0,0,0,0.10)",
    "& .MuiLinearProgress-bar": {
      backgroundColor: ({ colour }: { colour: string }) => colour,
      transition: "transform 250ms linear, background-color 400ms ease",
    },
  },
}));

/**
 * The real game. The server hands out a round id first (the game can only be played once per breef); the game's
 * score is sent with it, and the server computes the breef score. The progress bar follows the 30 seconds.
 */
const GameScenePage = () => {
  const [t] = useTranslation("global");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const breefDetails = useBreefDetails();
  const [round, setRound] = useState<{ round: string; seconds: number } | null>(null);
  const [progress, setProgress] = useState(0);
  const [failed, setFailed] = useState(false);
  const startedAt = useRef(0);
  // The last seconds turn red, so the time left is felt rather than read
  const classes = useStyles({ colour: progress > 85 ? "#D32F2F" : "#EB6608" });

  useEffect(() => {
    analytics.track("Game Scene Viewed", { current_page: "game-scene", game_name: breefDetails?.gameId?.gameName });
    startGameRound()
      .then(setRound)
      .catch((error) => {
        // Already played (reload, back button): straight to the result. Anything else (expired link, no network)
        // used to leave a white screen with no way on - now the participant is told and can carry on without it
        if (error?.response?.status === 409) navigate("/game-result");
        else setFailed(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!startedAt.current || !round) return;
    const timer = setInterval(() => setProgress(Math.min(100, ((Date.now() - startedAt.current) / (round.seconds * 1000)) * 100)), 250);
    return () => clearInterval(timer);
  }, [round, progress === 0 && startedAt.current]);

  // The game normally ends itself, but if it never says anything the round still has to end
  useEffect(() => {
    if (!round || !startedAt.current) return undefined;
    const guard = setTimeout(() => {
      if (!done.current) finish(0);
    }, (round.seconds + 8) * 1000);
    return () => clearTimeout(guard);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round, progress > 0]);

  const done = useRef(false);
  const finish = async (gameScore: number) => {
    if (!round || done.current) return;
    done.current = true;
    setProgress(100);
    try {
      const result = await submitGameScore(round.round, gameScore);
      sessionStorage.setItem("gameResult", JSON.stringify(result));
      sessionStorage.setItem("gameScore", String(result.gamePoints));
      sessionStorage.setItem("scoreUpdated", "true");
      dispatch(setScore(result.gamePoints));
      analytics.track("Game finished", { current_page: "game-scene", game_name: breefDetails?.gameId?.gameName, game_score: result.gamePoints, total_score: result.breefScore });
    } catch (error: any) {
      // Already submitted (e.g. two tabs): the result page shows what was saved
      if (error?.response?.status !== 409) console.error("Could not save the game score", error?.response?.data?.message || error);
    }
    // How this breef went, before the total score across all breefs
    setTimeout(() => navigate("/breef-score"), 2000);
  };

  return (
    <MobileLayout>
      <div className={classes.container}>
        <div className={classes.gameSpace}>
          {failed && (
            // The round could not be started: say so, and let the participant finish the breef anyway
            <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "100%", padding: "1.5rem", gap: "1rem", fontFamily: "Roboto, Arial, sans-serif", textAlign: "center" }}>
              <p style={{ margin: 0 }}>{t("gamePlay.loadFailed")}</p>
              <Button variant="contained" style={{ backgroundColor: "#EB6608", color: "#FFFFFF", textTransform: "none", height: "3rem", borderRadius: "10px", padding: "0 1.5rem" }} onClick={() => navigate("/breef-score")}>
                {t("gamePlay.continueWithout")}
              </Button>
            </div>
          )}
          {!failed && breefDetails?.gameId?.gameCode && round && (
            <GameFrame
              gameCode={breefDetails.gameId.gameCode}
              mode="real"
              seconds={round.seconds}
              round={round.round}
              onStarted={() => {
                startedAt.current = Date.now();
                setProgress(0.1);
              }}
              onScore={finish}
              onGiveUp={() => finish(0)}
            />
          )}
        </div>
        <LinearProgress variant="determinate" value={progress} className={classes.timeBar} style={{ opacity: progress > 0 ? 1 : 0.35 }} />
      </div>
    </MobileLayout>
  );
};

export default GameScenePage;
